import { applySeating, newGame } from '../game/index';
import { createGameStore, type GameStore } from '../game/store';
import { summarise } from '../game/summary';
import type { Game, LeaderboardInfo, Player } from '../game/types';
import {
  deleteLastRound,
  editLastDrawTenpai,
  editLastWinParticipants,
  endGame,
  recordDraw,
  recordWin,
  reopenGame,
} from '../game/record';
import { makeSettings, type HkosSettings, type ScoringMode } from '../rules/hkos';
import type { HandInput, WinContext } from '../rules/types';
import type { Wind } from '../rules/tiles';
import { DEFAULT_BRANDING, readBranding } from './branding';
import {
  GAME_START_LOCATION_TIMEOUT_MS,
  checkSession,
  getMaxScanAttempts,
  getSessionToken,
  hasFreshCachedSession,
  readLocation,
  type SessionCheck,
} from './activation';
import { hasPinSet, pinLockoutRemainingMs, setPin, verifyPin } from './pin';
import { renderAccessControl, renderActivationCodeOnly } from './views/accessControl';
import { renderActivation } from './views/activation';
import { renderAdminMenu, renderAdminMessage, renderAdminSecretPrompt } from './views/adminMenu';
import { renderTableLobby, renderTableOpen } from './views/leaderboardTable';
import { renderTableJoin, renderTableMessage, renderTableWaiting } from './views/tableJoin';
import { startUpdateWatch } from './updateWatch';
import { renderHandCalculator } from './views/handCalculator';
import { renderTableApproved, renderTableDeclined, renderTableWaitingApproval } from './views/tableApproval';
import { gameViewerKey, getViewer, rememberMyPlayer, rememberViewer, shareViewerKey } from './viewerIdentity';
import { resetPosterOffer } from './views/posters';
import { endedGameIsStale } from './landing';
import {
  TABLE_SIZE,
  buildTableLink,
  changePinAtTableApi,
  closeTableApi,
  fetchApprovedGame,
  fetchTableState,
  guestConsentGiven,
  joinTableApi,
  openTableApi,
  postTableShare,
  registerAtTableApi,
  rememberGuestConsent,
  removeTableMemberApi,
  reopenTableApi,
  startTableGame,
  tableTokenFromHash,
  type JoinedTable,
  type TableMember,
} from './tableClient';
import { renderLeaderboardAdmin } from './views/leaderboardAdmin';
import {
  renderLeaderboardMessage,
  renderLeaderboardNotice,
  renderChangePin,
  renderLeaderboardSetup,
} from './views/leaderboardSetup';
import {
  MSG_NETWORK,
  MSG_REACTIVATE,
  DEFAULT_PIN,
  changeLeaderboardPin,
  describeAccess,
  describeNotCounted,
  fetchActivePeriod,
  fetchDisplayBoard,
  leaderboardInfoFrom,
  periodRulesToSettings,
  registerLeaderboardPlayer,
  type LeaderboardPeriodInfo,
  type StartedLeaderboardGame,
  type SubmitOutcome,
} from './leaderboardClient';
import { showHouseRulesPreview } from './houseRulesPreview';
import {
  AdminAuthError,
  clearStoredAdminSecret,
  fetchAdminRole,
  fetchTableApprovalRequired,
  fetchTableRequests,
  fetchLeaderboardEnabled,
  fetchVenueAdminConfigured,
  generateVenueAdminSecretApi,
  getStoredAdminSecret,
  revokeVenueAdminApi,
  saveTableApprovalRequiredApi,
  saveLeaderboardEnabledApi,
  storeAdminSecret,
  useStaffSecret,
  type AdminRole,
} from './adminApi';
import { cachedLeaderboardEnabled, refreshLeaderboardEnabled } from './leaderboardFlag';
import {
  flushOwedResults,
  isAwaitingSubmission,
  owedGamesWith,
  sendGameResult,
  statusOf,
  type SubmissionStatus,
} from './leaderboardSubmit';
import { tvTokenFromHash } from './leaderboardDisplay';
import { mountTvDisplay, renderWebBoard } from './views/leaderboardDisplay';
import { paintLeaderboardStatus, type LeaderboardStatusActions } from './views/leaderboardStatus';
import { renderBrandingScreen } from './views/branding';
import { renderModeSelect } from './views/modeSelect';
import { describeSave, restoreBackupFile, saveBackupFile } from './backupFile';
import { renderPastGames } from './views/pastGames';
import { renderPhotoCapture } from './views/photoCapture';
import { mergeScanWithEntered } from '../vision/mergeScan';
import { renderPinGate, type PinGateMode } from './views/pinGate';
import { renderPlay, type Message, type PlayFormState, type PlayPrefill } from './views/play';
import { renderSettings, defaultHouseRules, type HouseRulesOverrides } from './views/settings';
import { renderSetup, type SeatingChoice, type SetupParams } from './views/setup';
import { renderSetSeating } from './views/seating';
import { renderShareGame } from './views/shareGame';
import {
  buildFakeGame,
  isGuestLiveScreen,
  refreshGuestRounds,
  renderGuestPlay,
  setGuestHeldPhotos,
  setGuestLiveNote,
  setGuestOutboxBanner,
  setGuestOwnPhotoNote,
  updateGuestLive,
  type GuestDrawPayload,
  type GuestPlayActions,
  type GuestWinPayload,
} from './views/guestPlay';
import {
  guestRoundsNote,
  guestRoundsWaiting,
  noteGuestRoundsChecked,
  setGuestRoundsWaiting,
  watchGuestRounds,
  watchSharedGame,
} from './shareWatch';
import { clearOutbox, describeOutbox, loadOutbox, noteFailedAttempt, saveOutbox, type OutboxItem } from './guestOutbox';
import { forgetApplied, loadApplied, markApplied } from './appliedGuestRounds';
import { dismissRejection } from './guestRejection';
import { retryWithBackoff } from './reliableSend';
import { startPoller, type Poller } from './livePoll';
import { previewFreshResultsPdf } from './resultsShare';
import { renderSummary } from './views/summary';
import { renderUsageLogView } from './views/usageLog';
import { clearUsageLog, readCostRates, readUsageLog, writeCostRates } from './usageLog';
import {
  clearAccuracyLog,
  countPendingRecognitions,
  handTiles,
  logRecognitionResult,
  readAccuracyLog,
  sanitiseSnapshot as sanitiseRecognition,
  summariseAccuracy,
} from './recognitionAccuracy';
import { getRecognitionEngine, setRecognitionEngine } from './recognitionEngine';
import { clearPendingScan, loadPendingScan } from '../vision/pendingScan';
import { initPhotoLightbox } from './lightbox';
import { initPatternGlossary } from './patternGlossary';
import { expandAllSettlementExplanations, initPrintExpand } from './settlementExplain';
import { applyTheme, readTheme } from './theme';
import { applyTextSize, readTextSize } from './textSize';
import { initThemePicker } from './themePicker';
import { deriveTableState, seatWindOf } from './rotation';
import { currentTotals } from './helpers';
import { renderBrandHeader } from './branding';
import { esc } from './helpers';
import {
  buildGuestLink,
  clearPendingSubmissionId,
  clearStoredShare,
  consumePending,
  createShare,
  fetchPending,
  fetchShareState,
  getStoredShare,
  publishState,
  regenerateShareLink,
  saveStoredShare,
  isRetryable,
  NETWORK_ERROR,
  submitRound as submitGuestRound,
  type PublishUpdate,
  type SharedGameState,
} from './gameShare';
import {
  GUEST_LINK_WINDOW_MS,
  addAwaitedPhoto,
  deferPayloadPhotos,
  heldPhotosNote,
  historyForGuests,
  enqueueGuestPhotoJob,
  isConnectionSlow,
  isSlim,
  loadGuestPhotoJobs,
  markConnectionSlow,
  photosStillHeld,
  setSlim,
  withFetchedPhotos,
} from './photoDelivery';
import {
  ensureAwaitedPhotoPickup,
  ensureGuestPhotoUpload,
  ensureRoundPhotoUploads,
  getHeldPhotos,
  loadedPhotosFor,
  type GuestPhotoOutcome,
} from './photoSync';

// A private, unguessable route — not "#admin". Anyone who doesn't already
// know this exact string has no way to discover the admin PIN screen at all.
const ADMIN_HASH = '#adminpaling_k3r3n_intheworld';

// The venue staff's own address (2026-09-25, Indra): straight to the players screen with the venue admin code — no admin menu,
// no device PIN. It only ever accepts the venue admin code; the owner's secret is refused here. Like ADMIN_HASH it is a
// deterrent, not the lock: what actually protects the screen is the code the server checks.
const STAFF_HASH = '#mahjongvenuestaff';

// A guest link's whole identity is its share token — no activation, no
// device session, nothing else to route on. See src/ui/gameShare.ts and
// relay/gameShare.ts for the two-token design this hash route leads into.
const GUEST_HASH_PREFIX = '#guest=';

const LAST_GAME_KEY = 'mahjong-scorer:lastGameId';
const LAST_TABLE_KEY = 'mahjong-scorer:lastTableId';
const LAST_NAMES_KEY = 'mahjong-scorer:lastPlayerNames';
const LAST_SETTINGS_KEY = 'mahjong-scorer:lastHouseRules';
const LAST_MODE_KEY = 'mahjong-scorer:lastScoringMode';
const LAST_SEATING_KEY = 'mahjong-scorer:lastSeatingChoice';

const SEATS: Wind[] = ['east', 'south', 'west', 'north'];

export async function mount(root: HTMLElement): Promise<void> {
  applyTheme(readTheme());
  applyTextSize(readTextSize());
  initPhotoLightbox();
  initPatternGlossary();
  initPrintExpand();
  initThemePicker();
  startUpdateWatch(); // every kind of screen, the venue TV included: notices a newer deploy (see updateWatch.ts)

  // The venue TV's address (#tv=<token>, made in Admin): read-only, no activation and no controls, authorised by its token alone —
  // a TV has no session. See relay/index.ts's /leaderboard/tv and views/leaderboardDisplay.ts.
  const tvToken = tvTokenFromHash(location.hash);
  if (tvToken) {
    mountTvDisplay(root, tvToken);
    return;
  }

  // A shared-game guest link bypasses activation entirely — that's the whole
  // point of the feature (see src/ui/gameShare.ts's header) — so it's
  // checked before anything else touches the normal device-session gate.
  if (location.hash.startsWith(GUEST_HASH_PREFIX)) {
    const shareToken = decodeURIComponent(location.hash.slice(GUEST_HASH_PREFIX.length));
    await showGuestPlay(root, shareToken);
    return;
  }

  // A table link (a player signing in to the host's leaderboard table): like a guest link it needs no activation code — the
  // venue is checked on the host's phone — and is authorised only by the table's own token. See src/ui/tableClient.ts.
  const tableToken = tableTokenFromHash(location.hash);
  if (tableToken) {
    await showTableJoin(root, tableToken);
    return;
  }

  const store = createGameStore();

  // The admin route is a separate, PIN-gated flow — never routed through the
  // player-facing activation gate. See src/ui/activation.ts's header.
  if (location.hash === ADMIN_HASH) {
    showAdminGate(root, store);
    return;
  }
  if (location.hash === STAFF_HASH) {
    useStaffSecret(); // the staff page keeps its own secret, apart from the owner's on the same device
    await openStaffPage(root);
    return;
  }

  if (!hasFreshCachedSession()) {
    showActivation(root, store);
    return;
  }

  // Any leaderboard result still owed (the game ended offline, or the relay was down) is retried when the
  // app opens and whenever the connection comes back — see leaderboardSubmit.ts.
  void flushOwedResults(store);
  window.addEventListener('online', () => void flushOwedResults(store));

  await routePlayerFacing(root, store);
}

/** Resume-in-progress / go-to-summary / fresh-start routing — the normal flow once a device is authenticated. */
async function routePlayerFacing(root: HTMLElement, store: GameStore): Promise<void> {
  const lastGameId = localStorage.getItem(LAST_GAME_KEY);
  const existing = lastGameId ? await store.load(lastGameId) : undefined;

  if (existing && !existing.endedAt) {
    showPlay(root, existing, store);
  } else if (existing?.endedAt) {
    // Last game's results stay for the day; after the activation code changes (or a leaderboard result is still unsent) they are not the first thing shown.
    if (endedGameIsStale(existing.endedAt, Date.now()) && !isAwaitingSubmission(existing)) showModeSelect(root, store);
    else showSummary(root, existing, store);
  } else {
    showModeSelect(root, store);
  }
}

function showActivation(root: HTMLElement, store: GameStore): void {
  renderActivation(root, {
    success: () => void routePlayerFacing(root, store),
  });
}

/** Falls back to the shipped defaults when nothing has been saved yet. */
function readHouseRules(): HouseRulesOverrides {
  const stored = localStorage.getItem(LAST_SETTINGS_KEY);
  if (!stored) return defaultHouseRules();
  return JSON.parse(stored) as HouseRulesOverrides;
}

function readLastMode(): ScoringMode {
  const stored = localStorage.getItem(LAST_MODE_KEY);
  return stored === 'fun' || stored === 'serious' ? stored : 'serious';
}

function showModeSelect(root: HTMLElement, store: GameStore): void {
  // Drawn at once from the last answer the relay gave; an admin's on/off switch is checked in the background
  // and the screen is redrawn (once) if it turns out to have changed.
  const showLeaderboard = cachedLeaderboardEnabled();
  renderModeSelect(
    root,
    readLastMode(),
    {
      choose: (mode) => {
        localStorage.setItem(LAST_MODE_KEY, mode);
        showSetup(root, store, mode);
      },
      viewPastGames: () => void showPastGames(root, store, () => showModeSelect(root, store)),
      viewHandCalculator: () => showHandCalculator(root, store, readLastMode()),
      openLeaderboard: () => void openLeaderboard(root, store),
      viewLeaderboard: () => void showLeaderboardBoard(root, store, () => showModeSelect(root, store)),
    },
    showLeaderboard,
  );
  void refreshLeaderboardEnabled().then((now) => {
    if (now === undefined || now === showLeaderboard) return;
    // Only if Mode Select is still what is on screen (the player may have moved on while this was in flight).
    if (root.querySelector('.mode-option[data-mode]')) showModeSelect(root, store);
  });
}

/** Every game this device has ever saved — reachable from Mode Select and from a just-ended game's Final Results. */
async function showPastGames(root: HTMLElement, store: GameStore, backTo: () => void, notice?: string): Promise<void> {
  const games = await store.listAll();
  renderPastGames(
    root,
    games,
    {
      open: (gameId) => void openPastGame(root, store, gameId, backTo),
      back: backTo,
      deleteGames: (gameIds) => void deletePastGames(root, store, gameIds, backTo),
      saveBackup: async () => describeSave(await saveBackupFile(await store.listAll())),
      restoreBackup: (file) =>
        void restoreBackupFile(file, store).then((result) => showPastGames(root, store, backTo, result.message)),
    },
    notice,
    summariseAccuracy(readAccuracyLog()),
  );
}

/** "Save This Game to a File" on Final Results: this one game, freshly read from the store so it is what is really saved. */
function saveThisGame(game: Game, store: GameStore): () => Promise<string> {
  return async () => describeSave(await saveBackupFile([(await store.load(game.id)) ?? game]));
}

/**
 * Permanently removes the given games from this device — used to clean up
 * old test/practice games. Also clears LAST_GAME_KEY / the cached share if
 * either happens to point at one of the deleted games, so nothing on this
 * device is left referencing a game that no longer exists (routePlayerFacing
 * already tolerates a dangling LAST_GAME_KEY gracefully via `store.load`
 * returning undefined, but there's no reason to leave it dangling).
 */
async function deletePastGames(root: HTMLElement, store: GameStore, gameIds: string[], backTo: () => void): Promise<void> {
  await Promise.all(gameIds.map((id) => store.delete(id)));

  if (gameIds.includes(localStorage.getItem(LAST_GAME_KEY) ?? '')) {
    localStorage.removeItem(LAST_GAME_KEY);
  }
  const cachedShare = getStoredShare();
  if (cachedShare && gameIds.includes(cachedShare.gameId)) {
    clearStoredShare();
  }

  void showPastGames(root, store, backTo);
}

/**
 * A finished game reopens its Final Results read-only (see `renderSummary`'s
 * `historyBack` param) — never as "the current game" (LAST_GAME_KEY is left
 * untouched). An unfinished one resumes exactly like the normal
 * continue-where-you-left-off flow, since a game left mid-way is mid-way
 * regardless of how you navigated to it.
 */
async function openPastGame(root: HTMLElement, store: GameStore, gameId: string, backTo: () => void): Promise<void> {
  const game = await store.load(gameId);
  if (!game) {
    void showPastGames(root, store, backTo);
    return;
  }
  if (!game.endedAt) {
    localStorage.setItem(LAST_GAME_KEY, game.id);
    showPlay(root, game, store);
    return;
  }
  const summary = summarise(game);
  renderSummary(
    root,
    game,
    summary,
    {
      newGame: () => {},
      resumeGame: () => {},
      saveGameFile: saveThisGame(game, store),
      leaderboard: leaderboardSummaryActions(root, store, game, () => void openPastGame(root, store, gameId, backTo), false),
    },
    () => void showPastGames(root, store, backTo),
  );
}

/** Leaves the hidden admin flow and returns to the normal player-facing app. */
function exitAdmin(root: HTMLElement, store: GameStore): void {
  history.replaceState(null, '', location.pathname + location.search);
  showModeSelect(root, store);
}

// Deliberately doesn't state how long the lockout lasts or count down — that
// would just hand an attacker the exact moment to resume guessing.
function lockoutMessage(): string | undefined {
  if (pinLockoutRemainingMs() <= 0) return undefined;
  return 'Too many wrong attempts.';
}

function showAdminGate(root: HTMLElement, store: GameStore): void {
  const mode: PinGateMode = hasPinSet() ? 'enter' : 'set';
  renderPinGate(root, mode, mode === 'enter' ? lockoutMessage() : undefined, {
    submit: (pin) => void handlePinSubmit(root, store, mode, pin),
    cancel: () => exitAdmin(root, store),
  });
}

async function handlePinSubmit(
  root: HTMLElement,
  store: GameStore,
  mode: PinGateMode,
  pin: string,
): Promise<void> {
  if (mode === 'set') {
    await setPin(pin);
    showAdminMenu(root, store);
    return;
  }

  if (await verifyPin(pin)) {
    showAdminMenu(root, store);
  } else {
    renderPinGate(root, 'enter', lockoutMessage() ?? 'Incorrect PIN.', {
      submit: (p) => void handlePinSubmit(root, store, 'enter', p),
      cancel: () => exitAdmin(root, store),
    });
  }
}

/** Which role the stored secret has, learnt from the server each time the menu opens; the screens under it read this. */
let adminRole: AdminRole = 'owner';

function showAdminMenu(root: HTMLElement, store: GameStore): void {
  void openAdminMenu(root, store);
}

/**
 * The admin menu needs to know WHO is asking — the owner, or venue staff with the limited access — so it asks for the
 * secret first (if this device doesn't have one), asks the server what it opens, and shows only that.
 */
async function openAdminMenu(root: HTMLElement, store: GameStore): Promise<void> {
  if (!getStoredAdminSecret()) return showAdminSecretPrompt(root, store);
  renderAdminMessage(root, 'Admin', 'Checking your access…', { exit: () => exitAdmin(root, store) });
  try {
    adminRole = await fetchAdminRole();
  } catch (e) {
    if (e instanceof AdminAuthError) return showAdminSecretPrompt(root, store, 'That code is not right.');
    return renderAdminMessage(root, 'Admin', 'Could not reach the server. Check your connection and try again.', {
      retry: () => void openAdminMenu(root, store),
      exit: () => exitAdmin(root, store),
    });
  }
  // This address is the owner's. Venue staff have their own (STAFF_HASH), so a venue code saved here — typed here by mistake, or
  // left over from before staff had their own address — is forgotten and the owner secret asked for instead.
  if (adminRole !== 'owner') {
    clearStoredAdminSecret();
    return showAdminSecretPrompt(root, store, 'That is a venue admin code. Venue staff use their own address — enter the owner secret here.');
  }
  renderAdminMenu(root, {
    role: adminRole,
    openBranding: () => showBranding(root, store),
    openUsageLog: () => showUsageLogView(root, store),
    openAccessControl: () => showAccessControl(root, store),
    openVenues: () => showVenues(root, store),
    openLeaderboard: () => showLeaderboardAdmin(root, store),
    loadWaitingCount: async () => (await fetchTableRequests()).length,
    loadLeaderboardEnabled: fetchLeaderboardEnabled,
    setLeaderboardEnabled: saveLeaderboardEnabledApi,
    loadApprovalRequired: fetchTableApprovalRequired,
    setApprovalRequired: saveTableApprovalRequiredApi,
    loadVenueAdminConfigured: fetchVenueAdminConfigured,
    createVenueAdminSecret: generateVenueAdminSecretApi,
    removeVenueAdmin: revokeVenueAdminApi,
    exit: () => exitAdmin(root, store),
  });
}

const STAFF_PROMPT_COPY = {
  title: 'Venue Staff',
  intro: 'Enter the venue admin code you were given. It is remembered on this device after that.',
  label: 'Venue admin code',
};

/** Leaving the staff page: back to the normal app (which asks for the activation code on a phone that has not got one). */
function exitStaff(): void {
  history.replaceState(null, '', location.pathname + location.search);
  location.reload();
}

/**
 * The venue staff address. Asks for the venue admin code, checks with the server what it opens, and goes straight to the
 * players screen. A code that opens more than that (the owner's) is refused and forgotten. The staff page keeps its own code
 * under its own key, so it never touches an owner secret saved on the same device.
 */
async function openStaffPage(root: HTMLElement): Promise<void> {
  const ask = (error?: string) =>
    renderAdminSecretPrompt(
      root,
      error,
      {
        submit: (secret) => {
          if (!secret) return ask('Enter the venue admin code.');
          storeAdminSecret(secret);
          void openStaffPage(root);
        },
        cancel: exitStaff,
      },
      STAFF_PROMPT_COPY,
    );
  if (!getStoredAdminSecret()) return ask();
  renderAdminMessage(root, 'Venue Staff', 'Checking your access…', { exit: exitStaff });
  let role: AdminRole;
  try {
    role = await fetchAdminRole();
  } catch (e) {
    if (e instanceof AdminAuthError) return ask('That code is not right.');
    return renderAdminMessage(root, 'Venue Staff', 'Could not reach the server. Check your connection and try again.', {
      retry: () => void openStaffPage(root),
      exit: exitStaff,
    });
  }
  if (role !== 'venue') {
    clearStoredAdminSecret();
    return ask('That is not a venue admin code. Enter the code you were given for venue staff.');
  }
  adminRole = 'venue';
  showStaffMenu(root);
}

/** What the staff address opens: Leaderboard and today's activation code (Indra, 2026-10-09). */
function showStaffMenu(root: HTMLElement): void {
  const notForStaff = () => undefined;
  renderAdminMenu(root, {
    role: 'venue',
    openLeaderboard: () => renderLeaderboardAdmin(root, { back: () => showStaffMenu(root), role: 'venue' }),
    openAccessControl: () => renderActivationCodeOnly(root, { back: () => showStaffMenu(root) }),
    loadWaitingCount: async () => (await fetchTableRequests()).length,
    // Never shown to staff (the menu draws them for the owner only).
    openBranding: notForStaff,
    openUsageLog: notForStaff,
    openVenues: notForStaff,
    loadLeaderboardEnabled: async () => true,
    setLeaderboardEnabled: async (enabled) => enabled,
    loadApprovalRequired: async () => true,
    setApprovalRequired: async (required) => required,
    loadVenueAdminConfigured: async () => true,
    createVenueAdminSecret: async () => '',
    removeVenueAdmin: async () => undefined,
    exit: exitStaff,
  });
}

function showAdminSecretPrompt(root: HTMLElement, store: GameStore, error?: string): void {
  renderAdminSecretPrompt(root, error, {
    submit: (secret) => {
      if (!secret) return showAdminSecretPrompt(root, store, 'Enter the admin secret.');
      storeAdminSecret(secret);
      void openAdminMenu(root, store);
    },
    cancel: () => exitAdmin(root, store),
  });
}

function showLeaderboardAdmin(root: HTMLElement, store: GameStore): void {
  renderLeaderboardAdmin(root, {
    back: () => showAdminMenu(root, store),
    role: adminRole,
  });
}

function showAccessControl(root: HTMLElement, store: GameStore): void {
  renderAccessControl(root, {
    back: () => showAdminMenu(root, store),
  });
}

/**
 * Dynamically imported — this screen's only consumer is Leaflet's embedded
 * map picker, and it would otherwise bloat the bundle every player
 * downloads just to scan a QR code and start a game. Loaded on demand, it
 * only ever costs an admin who actually opens this screen.
 */
function showVenues(root: HTMLElement, store: GameStore): void {
  // Leaflet (map picker) is a real, multi-hundred-KB download fetched only
  // on first visit to this screen — without an immediate loading state, the
  // button looked like it did nothing for however many seconds that took.
  root.innerHTML = `${renderBrandHeader()}<div class="card"><h1>Venues</h1><p class="muted">Loading…</p></div>`;
  import('./views/venues')
    .then(({ renderVenues }) => {
      renderVenues(root, {
        back: () => showAdminMenu(root, store),
      });
    })
    .catch(() => {
      root.innerHTML = `
        ${renderBrandHeader()}
        <div class="card">
          <h1>Venues</h1>
          <div class="error-box">Could not load this screen. Check your connection and try again.</div>
          <button type="button" id="venuesBackBtn" class="secondary" style="width:100%;margin-top:1rem">Back</button>
        </div>
      `;
      root.querySelector<HTMLButtonElement>('#venuesBackBtn')!.addEventListener('click', () => showAdminMenu(root, store));
    });
}

function showBranding(root: HTMLElement, store: GameStore): void {
  renderBrandingScreen(root, {
    exit: () => exitAdmin(root, store),
    cancel: () => showAdminMenu(root, store),
  });
}

function showUsageLogView(root: HTMLElement, store: GameStore): void {
  renderUsageLogView(
    root,
    readUsageLog(),
    readCostRates(),
    getRecognitionEngine(),
    { byEngine: summariseAccuracy(readAccuracyLog()), waiting: countPendingRecognitions() },
    {
      saveCostRates: (rates) => {
        writeCostRates(rates);
        showUsageLogView(root, store);
      },
      clearLog: () => {
        clearUsageLog();
        clearAccuracyLog();
        showUsageLogView(root, store);
      },
      setRecognitionEngine: (engine) => {
        setRecognitionEngine(engine);
        showUsageLogView(root, store);
      },
      back: () => showAdminMenu(root, store),
    },
  );
}

function showSetup(root: HTMLElement, store: GameStore, mode: ScoringMode): void {
  const lastTableId = localStorage.getItem(LAST_TABLE_KEY) ?? '';
  const lastNames = JSON.parse(localStorage.getItem(LAST_NAMES_KEY) ?? '[]') as string[];
  const lastSeating: SeatingChoice = localStorage.getItem(LAST_SEATING_KEY) === 'dice' ? 'dice' : 'entered';

  const startGameWithErrorHandling = (params: SetupParams) => {
    if (!params.tableId) {
      renderSetup(root, params, 'Enter a table ID.', {
        startGame: startGameWithErrorHandling,
        openSettings: () => showSettings(root, store, mode),
        changeMode: () => showModeSelect(root, store),
      });
      return;
    }
    if (params.playerNames.some((n) => !n)) {
      renderSetup(root, params, 'Enter all four player names.', {
        startGame: startGameWithErrorHandling,
        openSettings: () => showSettings(root, store, mode),
        changeMode: () => showModeSelect(root, store),
      });
      return;
    }
    void startGame(root, store, params, mode);
  };

  renderSetup(root, { tableId: lastTableId, playerNames: lastNames, seating: lastSeating }, undefined, {
    startGame: startGameWithErrorHandling,
    openSettings: () => showSettings(root, store, mode),
    changeMode: () => showModeSelect(root, store),
  });
}

/** The Hand Calculator (first screen): scores a hand with the last House Rules, records nothing. See views/handCalculator.ts. */
function showHandCalculator(root: HTMLElement, store: GameStore, mode: ScoringMode): void {
  renderHandCalculator(
    root,
    { mode, rules: readHouseRules() },
    {
      back: () => showModeSelect(root, store),
      openHouseRules: (m) =>
        renderSettings(root, m, readHouseRules(), undefined, {
          // The same editor the setup screen uses: what is saved here is what the next game starts with too.
          save: (overrides) => {
            localStorage.setItem(LAST_SETTINGS_KEY, JSON.stringify(overrides));
            showHandCalculator(root, store, m);
          },
          cancel: () => showHandCalculator(root, store, m),
        }),
    },
  );
}

function showSettings(root: HTMLElement, store: GameStore, mode: ScoringMode): void {
  renderSettings(root, mode, readHouseRules(), undefined, {
    save: (overrides) => {
      // Only seeds the *next* makeSettings() call in startGame(); a Game's
      // settings snapshot, once taken, is never touched by later edits here.
      localStorage.setItem(LAST_SETTINGS_KEY, JSON.stringify(overrides));
      showSetup(root, store, mode);
    },
    cancel: () => showSetup(root, store, mode),
  });
}

/**
 * Starting a fresh game is the periodic-recheck boundary (not every scan,
 * not every app open — see the design discussion on why). A device that's
 * moved to somewhere unexpected since it last authenticated gets caught
 * here, before the new game is created; a device that's still fine sails
 * through with no visible friction beyond a brief pause.
 */
async function startGame(
  root: HTMLElement,
  store: GameStore,
  params: { tableId: string; playerNames: [string, string, string, string]; seating: SeatingChoice },
  mode: ScoringMode,
): Promise<void> {
  // A phone that knows it has no connection (airplane mode) does not wait for a location or a server that cannot answer: it starts at once.
  let check: SessionCheck = 'unreachable';
  if (navigator.onLine !== false) {
    const geo = await readLocation(GAME_START_LOCATION_TIMEOUT_MS);
    check = await checkSession(geo.ok ? geo.coords : undefined, params.tableId);
  }
  if (check === 'invalid') {
    showActivation(root, store);
    return;
  }

  // No answer from the server is not a reason to stop a game: scoring, the hand tools and the on-device photo scan all run on this
  // phone. The game starts, and says plainly what it could not do (Indra, 2026-10-06).
  await createAndOpenGame(
    root,
    store,
    {
      tableId: params.tableId,
      playerNames: params.playerNames,
      seating: params.seating,
      settings: makeSettings({ ...readHouseRules(), mode }),
    },
    check === 'unreachable' ? OFFLINE_START_NOTICE : undefined,
  );
}

/** A leaderboard game cannot start without the server: its table, players and result all go through it. */
const LEADERBOARD_NEEDS_CONNECTION = 'The leaderboard needs a connection to the server. Check your connection and try again.';

/** Shown at the top of the scoring screen when a game was started with no connection to the server. */
export const OFFLINE_START_NOTICE =
  'No connection — this game started without checking in. Scoring works as normal. Sharing the game with other phones is not available until the connection is back; then tap “Share This Game…”, no restart needed.';

/**
 * Builds the game, remembers this table's setup, and opens the play screen. Shared by a normal game (settings
 * from the device's own House Rules) and a Leaderboard game (settings frozen by the venue's period).
 */
async function createAndOpenGame(
  root: HTMLElement,
  store: GameStore,
  params: {
    tableId: string;
    playerNames: string[];
    seating: SeatingChoice;
    settings: HkosSettings;
    leaderboard?: LeaderboardInfo;
  },
  notice?: string,
): Promise<Game> {
  const players: Player[] = params.playerNames.map((name, i) => ({
    id: `p${i + 1}`,
    name,
    seat: SEATS[i]!,
  }));

  const game = newGame({
    id: crypto.randomUUID(),
    tableId: params.tableId,
    rulesetId: 'hkos',
    settings: params.settings,
    players,
    // "Decide with the dice": the names were typed in any order, so the
    // game waits (seatingPending) until the host assigns the winds.
    seatingByDice: params.seating === 'dice',
    ...(params.leaderboard ? { leaderboard: params.leaderboard } : {}),
  });

  localStorage.setItem(LAST_GAME_KEY, game.id);
  localStorage.setItem(LAST_TABLE_KEY, params.tableId);
  localStorage.setItem(LAST_NAMES_KEY, JSON.stringify(params.playerNames));
  localStorage.setItem(LAST_SEATING_KEY, params.seating);

  // A share link from a previous game must never resolve into this new one —
  // its gameId won't match, so showShareGame would mint a fresh share anyway,
  // but clearing it here avoids leaving a dead reference lying around.
  clearStoredShare();

  await store.save(game);
  showPlay(root, game, store, undefined, notice ? { kind: 'notice', text: notice } : undefined);
  return game;
}

// ---------------------------------------------------------------------------
// Leaderboard mode — the venue's leaderboard (relay/leaderboard.ts). A leaderboard game IS a normal HKOS
// game, played under the period's frozen rules; it just also holds what step 4 needs to submit its result.
// ---------------------------------------------------------------------------

async function openLeaderboard(root: HTMLElement, store: GameStore): Promise<void> {
  const back = () => showModeSelect(root, store);
  renderLeaderboardMessage(root, 'Leaderboard', 'Checking for a running leaderboard…', undefined);
  const geo = await readLocation();
  const found = await fetchActivePeriod(geo.ok ? geo.coords : undefined);
  if (!found.ok) {
    if (found.error === MSG_REACTIVATE) return showActivation(root, store);
    return renderLeaderboardMessage(root, 'Leaderboard', found.error, { back, retry: () => void openLeaderboard(root, store) });
  }
  // The relay judged the device by where it is RIGHT NOW, so say so before anything else — even if a period exists.
  if (found.access !== 'ok') {
    return renderLeaderboardMessage(root, 'Leaderboard', describeAccess(found.access), {
      back,
      ...(found.access === 'location_needed' ? { retry: () => void openLeaderboard(root, store) } : {}),
    });
  }
  if (!found.period) return renderLeaderboardMessage(root, 'No leaderboard mode right now', '', { back });
  const rules = periodRulesToSettings(found.period);
  if (!rules.ok) return renderLeaderboardMessage(root, 'Leaderboard', rules.error, { back });
  showLeaderboardSetup(root, store, found.period, rules.settings);
}

function showLeaderboardSetup(
  root: HTMLElement,
  store: GameStore,
  period: LeaderboardPeriodInfo,
  settings: HkosSettings,
  error?: string,
  notice?: string,
): void {
  const here = () => showLeaderboardSetup(root, store, period, settings);
  renderLeaderboardSetup(
    root,
    period,
    error,
    {
      register: async (nickname, pin) => {
        const result = await registerLeaderboardPlayer(nickname, pin);
        if (!result.ok) return { ok: false, message: result.error };
        return result.status === 'pending'
          ? {
              ok: true,
              pending: true,
              message: `Request sent for ${result.nickname}. The staff need to approve you before you can play — ask at the desk, then sign in on your own phone.`,
            }
          : { ok: true, message: `Joined as ${result.nickname}. Sign in with this nickname and PIN on your own phone when the host opens a table.` };
      },
      signInOnPhones: () => showTableOpen(root, store, period, settings),
      showRules: () => showHouseRulesPreview(settings, 'Leaderboard rules'),
      viewBoard: () => void showLeaderboardBoard(root, store, here),
      changePin: () => showChangePin(root, store, period, settings, { nickname: '', forced: false, back: here }),
      back: () => showModeSelect(root, store),
    },
    notice,
  );
}

/**
 * Choosing a new PIN. Reached two ways: automatically, when starting a table names a player whose account staff have
 * unlocked or given a temporary PIN (`forced`: the nickname and the PIN just typed are carried over, in memory only, so the
 * player is only asked for the new one), or from the "Change my PIN" button.
 */
function showChangePin(
  root: HTMLElement,
  store: GameStore,
  period: LeaderboardPeriodInfo,
  settings: HkosSettings,
  opts: {
    nickname: string;
    currentPin?: string;
    forced: boolean;
    back: () => void;
    error?: string;
  },
): void {
  // Re-draws with what the player already typed (the nickname, in the voluntary form) so an error never costs them their typing.
  const retry = (message: string, nickname = opts.nickname) => showChangePin(root, store, period, settings, { ...opts, nickname, error: message });
  renderChangePin(root, { nickname: opts.nickname, currentPin: opts.currentPin, forced: opts.forced }, opts.error, {
    back: opts.back,
    submit: (p) =>
      void (async () => {
        const fail = (message: string) => retry(message, p.nickname || opts.nickname);
        if (!p.nickname) return fail('Enter your nickname.');
        if (!/^\d{4}$/.test(p.pin)) return fail('Enter your current PIN — 4 digits.');
        if (!/^\d{4}$/.test(p.newPin)) return fail('The new PIN must be exactly 4 digits.');
        if (p.newPin !== p.newPin2) return fail('The two new PINs are different.');
        if (p.newPin === p.pin) return fail('Choose a different PIN from the one you have now.');
        if (p.newPin === DEFAULT_PIN) return fail('Choose a PIN of your own — not the standard temporary one.');
        const result = await changeLeaderboardPin(p.nickname, p.pin, p.newPin);
        if (!result.ok) {
          if (result.error === MSG_REACTIVATE) return showActivation(root, store);
          return fail(result.error);
        }
        showLeaderboardSetup(root, store, period, settings, undefined, `New PIN saved for ${result.nickname}. You can use it now.`);
      })(),
  });
}

/**
 * A leaderboard game the relay has just started from a table the players signed in to: read the period's rules, warn about
 * seats that won't count, then open the game.
 */
function beginLeaderboardGame(
  root: HTMLElement,
  store: GameStore,
  started: StartedLeaderboardGame,
  /** `tableToken` = the game came from a sign-in table: once it is open, its guest link is sent to the players' phones. */
  opts: { tableId: string; seating: SeatingChoice; tableToken?: string; viewer?: string },
  again: (message?: string) => void,
): void {
  // The period the relay just started the game in is the one whose rules bind this game.
  const rules = periodRulesToSettings(started.period);
  if (!rules.ok) return again(rules.error);

  const begin = () =>
    void createAndOpenGame(root, store, {
      tableId: opts.tableId,
      playerNames: started.players,
      seating: opts.seating,
      settings: rules.settings,
      leaderboard: leaderboardInfoFrom(started, Date.now()),
    }).then((game) => {
      // This phone signed in to the table as one player: remember who, for the end-of-game offer of their own poster.
      if (opts.viewer) rememberViewer(gameViewerKey(game.id), opts.viewer);
      if (opts.tableToken) void connectPlayers(root, store, game, opts.tableToken);
    });
  if (started.notCounted.length > 0) {
    renderLeaderboardNotice(root, describeNotCounted(started.notCounted), { begin, cancel: () => again() });
  } else {
    begin();
  }
}

// ---------------------------------------------------------------------------
// Sign-in tables: the host opens a table and the other players sign in on their own phones (relay/leaderboardTable.ts).
// ---------------------------------------------------------------------------

/**
 * Runs `tick` every `ms` for as long as `marker` is still on the page; `tick` returns true to stop. One run at a time, so a slow
 * connection never stacks up requests.
 */
function pollWhileShown(root: HTMLElement, marker: Element | null, ms: number, tick: () => Promise<boolean | void>): void {
  let busy = false;
  const timer = setInterval(() => {
    if (!marker || !root.contains(marker)) return clearInterval(timer);
    if (busy) return;
    busy = true;
    void tick()
      .then((stop) => {
        if (stop) clearInterval(timer);
      })
      .finally(() => {
        busy = false;
      });
  }, ms);
}

/**
 * Like `pollWhileShown`, but for a run of table screens that replace one another: it goes on for as long as SOME table screen
 * (a card marked `data-table-screen`) is on the page, and stops the moment the player leaves for anything else.
 */
function pollWhileTableScreen(root: HTMLElement, ms: number, tick: () => Promise<boolean | void>): void {
  let busy = false;
  const timer = setInterval(() => {
    if (!root.querySelector('[data-table-screen]')) return clearInterval(timer);
    if (busy) return;
    busy = true;
    void tick()
      .then((stop) => {
        if (stop) clearInterval(timer);
      })
      .finally(() => {
        busy = false;
      });
  }, ms);
}

const TABLE_POLL_MS = 3000;
const OWED_RESULT_MESSAGE =
  "The result of your last leaderboard game hasn't been sent yet. Check your connection and try again — it has to be sent before a new leaderboard game can start.";

function showTableOpen(root: HTMLElement, store: GameStore, period: LeaderboardPeriodInfo, settings: HkosSettings, error?: string, nickname = ''): void {
  renderTableOpen(root, period, { nickname }, error, {
    back: () => showLeaderboardSetup(root, store, period, settings),
    open: (p) => void openTable(root, store, period, settings, p),
  });
}

async function openTable(
  root: HTMLElement,
  store: GameStore,
  period: LeaderboardPeriodInfo,
  settings: HkosSettings,
  p: { nickname: string; pin: string },
): Promise<void> {
  const again = (message: string) => showTableOpen(root, store, period, settings, message, p.nickname);
  if (!p.nickname) return again('Enter your nickname.');
  if (!/^\d{4}$/.test(p.pin)) return again('Enter your 4-digit PIN.');

  await flushOwedResults(store);
  if (owedGamesWith(await store.listAll(), [p.nickname]).length > 0) return again(OWED_RESULT_MESSAGE);

  const geo = await readLocation();
  const coords = geo.ok ? geo.coords : undefined;
  const check = await checkSession(coords);
  if (check === 'invalid') return showActivation(root, store);
  if (check === 'unreachable') return again(LEADERBOARD_NEEDS_CONNECTION);

  const result = await openTableApi(p.nickname, p.pin, coords);
  if (!result.ok) {
    if (result.error === MSG_REACTIVATE) return showActivation(root, store);
    if (result.code === 'pin_change_required') {
      // Staff unlocked the host's own account: choose a PIN first, then open the table.
      return showChangePin(root, store, period, settings, {
        nickname: result.player ?? p.nickname,
        currentPin: p.pin,
        forced: true,
        back: () => again(''),
      });
    }
    return again(result.error);
  }
  showTableLobby(root, store, period, settings, {
    token: result.token,
    members: result.members,
    approvalRequired: result.approvalRequired,
    hostNickname: result.members.find((m) => m.isHost)?.nickname ?? p.nickname,
  });
}

/** The host's lobby: the QR, who has signed in, the seating, Start. Polls the table until the game starts or the host leaves. */
function showTableLobby(
  root: HTMLElement,
  store: GameStore,
  period: LeaderboardPeriodInfo,
  settings: HkosSettings,
  table: { token: string; members: TableMember[]; approvalRequired: boolean; hostNickname: string },
): void {
  const back = (message?: string) => showLeaderboardSetup(root, store, period, settings, message);
  const controller = renderTableLobby(
    root,
    {
      period,
      link: buildTableLink(table.token),
      approvalRequired: table.approvalRequired,
      defaults: {
        tableId: localStorage.getItem(LAST_TABLE_KEY) ?? '',
        seating: localStorage.getItem(LAST_SEATING_KEY) === 'dice' ? 'dice' : 'entered',
      },
    },
    table.members,
    {
      start: (p) => void startTableFromLobby(root, store, period, settings, table.token, table.hostNickname, controller, p),
      remove: (nickname) => void removeTableMemberApi(table.token, nickname).then(() => refresh()),
      cancel: () => void closeTableApi(table.token).finally(() => back()),
    },
  );

  const refresh = async (): Promise<boolean> => {
    const res = await fetchTableState(table.token);
    if (!res.ok) return false; // no signal for a moment: keep what is on screen
    if (res.view.status === 'open') {
      controller.update(res.view.members);
      controller.setApprovalRequired(res.view.approvalRequired);
      return false;
    }
    if (res.view.status === 'closed') {
      back('This table has closed. Open a new one to try again.');
      return true;
    }
    // Just sent to the staff (this screen is about to be replaced) or already started: nothing for the lobby to do.
    return res.view.status !== 'awaiting_approval';
  };
  pollWhileShown(root, root.querySelector('#tableLobby'), TABLE_POLL_MS, refresh);
}

async function startTableFromLobby(
  root: HTMLElement,
  store: GameStore,
  period: LeaderboardPeriodInfo,
  settings: HkosSettings,
  token: string,
  hostNickname: string,
  lobby: { showError(message: string): void; ready(): void },
  p: { tableId: string; seating: SeatingChoice; players: string[] },
): Promise<void> {
  const fail = (message: string) => {
    lobby.showError(message);
    lobby.ready();
  };
  if (!p.tableId) return fail('Enter a table ID.');
  if (p.players.length !== TABLE_SIZE || p.players.some((n) => !n)) return fail('Four players are needed.');

  // Same as any leaderboard start: a finished game of theirs still unsent would be lost when they start another.
  await flushOwedResults(store);
  if (owedGamesWith(await store.listAll(), p.players).length > 0) return fail(OWED_RESULT_MESSAGE);

  const geo = await readLocation();
  const coords = geo.ok ? geo.coords : undefined;
  const check = await checkSession(coords, p.tableId);
  if (check === 'invalid') return showActivation(root, store);
  if (check === 'unreachable') return fail(LEADERBOARD_NEEDS_CONNECTION);

  const result = await startTableGame(token, p.players, { tableName: p.tableId, seating: p.seating }, coords);
  if (!result.ok) {
    if (result.error === MSG_REACTIVATE) return showActivation(root, store);
    return fail(result.error);
  }
  const back = (message?: string) => showLeaderboardSetup(root, store, period, settings, message);
  if (result.pending) {
    // With the staff approval on, nothing starts yet: everyone at the table waits, locked, until the staff answer.
    return showHostApproval(root, store, period, settings, { token, tableName: p.tableId, players: p.players, seating: p.seating, hostNickname });
  }
  beginLeaderboardGame(root, store, result.game, { tableId: p.tableId, seating: p.seating, tableToken: token, viewer: hostNickname }, back);
}

/**
 * The host's phone while the staff decide. Nothing on it can be tapped (Indra's spec); it changes by itself: the staff approve (the
 * host gets an OK button that opens the scoring screen), or decline (the host can take the table back and send it again).
 */
function showHostApproval(
  root: HTMLElement,
  store: GameStore,
  period: LeaderboardPeriodInfo,
  settings: HkosSettings,
  t: { token: string; tableName: string; players: string[]; seating: SeatingChoice; hostNickname: string },
): void {
  const back = (message?: string) => showLeaderboardSetup(root, store, period, settings, message);
  renderTableWaitingApproval(root, { tableName: t.tableName, players: t.players, role: 'host' });
  pollWhileTableScreen(root, TABLE_POLL_MS, async () => {
    const res = await fetchTableState(t.token);
    if (!res.ok) return false; // no signal for a moment: keep what is on screen
    const view = res.view;
    if (view.status === 'awaiting_approval') return false;
    if (view.status === 'closed') {
      back('This table has closed. Open a new one to try again.');
      return true;
    }
    if (view.status === 'declined') {
      renderTableDeclined(root, {
        role: 'host',
        onBack: () =>
          void reopenTableApi(t.token).then((r) => {
            if (!r.ok) return back(r.error);
            showTableLobby(root, store, period, settings, { token: t.token, members: r.members, approvalRequired: true, hostNickname: t.hostNickname });
          }),
      });
      return true;
    }
    // Approved (or, if the host phone was slow, already started): fetch the game the staff approved and celebrate.
    const game = await fetchApprovedGame(t.token);
    if (!game.ok) return false;
    renderTableApproved(root, {
      role: 'host',
      tableName: t.tableName,
      byStaff: true,
      onOk: () => beginLeaderboardGame(root, store, game.game, { tableId: t.tableName, seating: t.seating, tableToken: t.token, viewer: t.hostNickname }, back),
    });
    return true;
  });
}

/**
 * The game is open on the host's phone: make its guest link and hand it to the players' phones through the table, so their screens
 * open on the same game. If that fails the host is told and can still use "Share This Game".
 */
async function connectPlayers(root: HTMLElement, store: GameStore, game: Game, tableToken: string): Promise<void> {
  const shared = await ensureGameShare(game);
  const posted = shared.ok ? await postTableShare(tableToken, shared.shareToken) : shared;
  if (posted.ok) return;
  const why = 'error' in posted ? posted.error : 'Try again.';
  showPlay(root, game, store, undefined, {
    kind: 'error',
    text: `The players' screens could not be connected (${why}). Tap Share This Game and send them the link instead.`,
  });
}

// --- a player's own phone --------------------------------------------------------------------------------------------------------------

/** The consent period the table being joined reported (see `guestConsentGiven`). Empty = ask every time. */
let guestConsentPeriod = '';

async function showTableJoin(root: HTMLElement, token: string): Promise<void> {
  const state = await fetchTableState(token);
  if (!state.ok) {
    // Switched off by an admin (an old table link still on someone's phone): the connection is fine, so say that, and offer no retry.
    if (state.code === 'disabled') {
      return renderTableMessage(root, 'Leaderboard is not available currently', 'This table link cannot be used right now. Please ask the venue staff.');
    }
    return renderTableMessage(root, 'No connection', state.error || MSG_NETWORK, { label: 'Try again', run: () => void showTableJoin(root, token) });
  }
  const view = state.view;
  if (view.status === 'closed') {
    return renderTableMessage(root, 'This table is closed', 'This table is no longer open. Ask the host to open a new one.');
  }
  if (view.status === 'started' || view.status === 'approved') {
    return renderTableMessage(root, 'The game has started', 'The host has already started the game at this table.');
  }
  if (view.status !== 'open') {
    return renderTableMessage(root, 'This table is with the staff', 'The host has sent this table for approval, so no more players can join it.');
  }
  // Which consent period the table is in (it changes with the activation code): a guest who agreed in this one is not asked again.
  guestConsentPeriod = view.consentPeriod;
  showTableJoinForm(root, token, {});
}

function showTableJoinForm(
  root: HTMLElement,
  token: string,
  opts: { mode?: 'existing' | 'new'; nickname?: string; error?: string; notice?: string },
): void {
  const CONSENT = 'Please tick the box to agree before continuing.';
  renderTableJoin(root, { ...opts, consentGiven: guestConsentGiven(guestConsentPeriod) }, {
    signIn: (p) =>
      void (async () => {
        const fail = (error: string) => showTableJoinForm(root, token, { mode: 'existing', nickname: p.nickname, error });
        if (!p.nickname) return fail('Enter your nickname.');
        if (!/^\d{4}$/.test(p.pin)) return fail('Enter your 4-digit PIN.');
        if (!p.agreed) return fail(CONSENT);
        await signInAtTable(root, token, p.nickname, p.pin, fail);
      })(),
    register: (p) =>
      void (async () => {
        const fail = (error: string) => showTableJoinForm(root, token, { mode: 'new', nickname: p.nickname, error });
        if (!p.nickname) return fail('Choose a nickname.');
        if (!/^\d{4}$/.test(p.pin)) return fail('A PIN is exactly 4 digits.');
        if (p.pin !== p.pin2) return fail('The two PINs are different.');
        if (!p.agreed) return fail(CONSENT);
        const result = await registerAtTableApi(token, p.nickname, p.pin, true);
        if (!result.ok) return fail(result.error);
        rememberGuestConsent(guestConsentPeriod);
        if (result.status === 'pending') {
          return showTableJoinForm(root, token, {
            mode: 'existing',
            nickname: result.nickname,
            notice: `Request sent for ${result.nickname}. Once the staff approve it, sign in here with your nickname and PIN.`,
          });
        }
        await signInAtTable(root, token, result.nickname, p.pin, fail);
      })(),
  });
}

/** Signs in with a nickname and PIN (consent was already ticked). A player the staff just unlocked is sent to choose a PIN first. */
async function signInAtTable(root: HTMLElement, token: string, nickname: string, pin: string, fail: (error: string) => void): Promise<void> {
  const result = await joinTableApi(token, nickname, pin, true);
  if (result.ok) {
    rememberGuestConsent(guestConsentPeriod); // agreed today: not asked again until the activation code changes
    return showTableWaiting(root, token, result);
  }
  if (result.code === 'table_closed') return renderTableMessage(root, 'This table is closed', result.error);
  if (result.code === 'pin_change_required') return showTableChangePin(root, token, result.player ?? nickname, pin);
  fail(result.error);
}

function showTableChangePin(root: HTMLElement, token: string, nickname: string, currentPin: string, error?: string): void {
  renderChangePin(root, { nickname, currentPin, forced: true }, error, {
    back: () => showTableJoinForm(root, token, { mode: 'existing', nickname }),
    submit: (p) =>
      void (async () => {
        const retry = (message: string) => showTableChangePin(root, token, nickname, currentPin, message);
        if (!/^\d{4}$/.test(p.newPin)) return retry('The new PIN must be exactly 4 digits.');
        if (p.newPin !== p.newPin2) return retry('The two new PINs are different.');
        if (p.newPin === currentPin) return retry('Choose a different PIN from the one you have now.');
        if (p.newPin === DEFAULT_PIN) return retry('Choose a PIN of your own — not the standard temporary one.');
        const changed = await changePinAtTableApi(token, nickname, currentPin, p.newPin);
        if (!changed.ok) return retry(changed.error);
        // Their own PIN is set: sign in with it straight away.
        await signInAtTable(root, token, nickname, p.newPin, (message) => showTableJoinForm(root, token, { mode: 'existing', nickname, error: message }));
      })(),
  });
}

/** A guest whose table has a game: their phone opens the same game through its guest link (scoring and tile screen included). */
function openGuestGame(root: HTMLElement, shareToken: string, nickname: string): void {
  rememberViewer(shareViewerKey(shareToken), nickname); // so the end of the game can offer this player their own poster
  history.replaceState(null, '', `#guest=${encodeURIComponent(shareToken)}`);
  void showGuestPlay(root, shareToken);
}

/**
 * A guest's phone from the moment they are signed in until the game opens. One loop follows the table and shows the right screen:
 * the sign-in list, "waiting for the staff" (locked, nothing to tap), approved, not approved, and finally the game itself.
 */
function showTableWaiting(root: HTMLElement, token: string, joined: JoinedTable): void {
  const notice =
    joined.notCounted === 'daily_limit'
      ? 'You have reached today’s limit, so this game will not count toward the leaderboard for you. You can still play.'
      : joined.notCounted === 'period_limit'
        ? 'You have reached the limit for this whole period, so this game will not count toward the leaderboard for you. You can still play.'
        : undefined;
  let shown = '';
  let list: { update(members: TableMember[]): void } | undefined;
  let tableName = '';
  const showList = (members: TableMember[]) => {
    if (shown === 'open' && list) return list.update(members);
    list = renderTableWaiting(root, { nickname: joined.nickname, members, ...(notice ? { notice } : {}) });
    shown = 'open';
  };
  showList(joined.members);
  pollWhileTableScreen(root, TABLE_POLL_MS, async () => {
    const res = await fetchTableState(token);
    if (!res.ok) return false; // keep showing what is known
    const view = res.view;
    switch (view.status) {
      case 'open':
        if (!view.members.some((m) => m.nickname === joined.nickname)) {
          renderTableMessage(root, 'You are no longer at this table', 'The host took you off this table. Sign in again if that was a mistake.', {
            label: 'Sign in again',
            run: () => showTableJoinForm(root, token, { mode: 'existing', nickname: joined.nickname }),
          });
          return true;
        }
        showList(view.members);
        return false;
      case 'awaiting_approval':
        tableName = view.tableName;
        if (shown !== 'awaiting') {
          renderTableWaitingApproval(root, { tableName: view.tableName, players: view.players, role: 'guest' });
          shown = 'awaiting';
        }
        return false;
      case 'declined':
        if (shown !== 'declined') {
          renderTableDeclined(root, { role: 'guest' });
          shown = 'declined';
        }
        return false;
      case 'approved':
      case 'started':
        if (view.status === 'started' && view.shareToken) {
          openGuestGame(root, view.shareToken, joined.nickname);
          return true;
        }
        if (shown !== 'approved') {
          renderTableApproved(root, { role: 'guest', tableName, byStaff: view.status === 'started' || view.byStaff });
          shown = 'approved';
        }
        return false;
      case 'closed':
        renderTableMessage(root, 'This table is closed', 'This table is no longer open. Ask the host to open a new one.');
        return true;
    }
  });
}

function showPlay(root: HTMLElement, game: Game, store: GameStore, prefill?: PlayPrefill, message?: Message): void {
  // The game is being played (again): ending it later offers the player their poster again, like the confetti.
  resetPosterOffer(gameViewerKey(game.id));
  const pendingScan = prefill ? undefined : loadPendingScan(game.tableId);
  renderPlay(
    root,
    game,
    store,
    prefill,
    message,
    {
      onEndGame: () => {
        endGame(game);
        publishGameEndedState(game, true);
        void store.save(game).then(() => showSummary(root, game, store));
      },
      onScanPhoto: (current) => void showPhotoCapture(root, game, store, undefined, current),
      onResumeScan: () => void showPhotoCapture(root, game, store, pendingScan?.photo),
      onDiscardScan: () => {
        clearPendingScan(game.tableId);
        showPlay(root, game, store);
      },
      onEditLastWin: (participants) => {
        editLastWinParticipants(game, participants);
        publishGameStateToShare(game);
        void store.save(game).then(() => showPlay(root, game, store));
      },
      onEditLastDrawTenpai: (tenpai) => {
        editLastDrawTenpai(game, tenpai);
        publishGameStateToShare(game);
        void store.save(game).then(() => showPlay(root, game, store));
      },
      onDeleteLastRound: () => {
        deleteLastRound(game);
        publishGameStateToShare(game);
        void store.save(game).then(() => showPlay(root, game, store));
      },
      onSetSeating: () => showSetSeating(root, game, store),
      onShareGame: () => void showShareGame(root, game, store),
      onCheckSubmissions: () => void checkGuestSubmissions(root, game, store),
      onRoundRecorded: () => {
        publishGameStateToShare(game);
        // A round the host entered itself (rounds applied from a guest do not come through here): that winner is a player this phone plays for.
        const last = game.rounds[game.rounds.length - 1];
        if (last?.outcome === 'win' && last.winner) rememberMyPlayer(gameViewerKey(game.id), last.winner);
      },
      guestRoundsWaiting,
      guestRoundsNote,
      photoSyncNote: () => photoSyncNoteFor(game),
    },
    !!pendingScan,
  );
  // A shared game shows "how many guest rounds are waiting" on its button, kept current in the background.
  watchGuestRounds(game.id);
  resumePhotoSync(game, store);
}

/**
 * The "Set Seating" screen for a game started with "Decide with the dice".
 * Nothing checks the dice — the host just says who sits where — and once
 * confirmed the guests' copy is updated so they can start recording rounds.
 */
function showSetSeating(root: HTMLElement, game: Game, store: GameStore): void {
  renderSetSeating(root, game, {
    confirm: (assignment) => {
      applySeating(game, assignment);
      publishSeatingToShare(game);
      void store.save(game).then(() => showPlay(root, game, store));
    },
    cancel: () => showPlay(root, game, store),
  });
}

/**
 * Main-device side of the "share this game" feature. Reuses a share already
 * created for this exact game if one is cached; otherwise mints a fresh one
 * (needs an active activation session — a device that's lost its session
 * shouldn't be able to hand out a link either). See `src/ui/gameShare.ts`.
 */
/**
 * The game's guest link: the one already made for this exact game, or a new one (needs an active activation session — a device that
 * has lost its session shouldn't be able to hand out a link either). Shared by "Share This Game" and by a leaderboard table, which
 * sends the link to the players' phones by itself.
 */
async function ensureGameShare(game: Game): Promise<{ ok: true; shareToken: string } | { ok: false; error: string; noSession?: boolean }> {
  const cached = getStoredShare();
  if (cached && cached.gameId === game.id) return { ok: true, shareToken: cached.shareToken };

  const sessionToken = getSessionToken();
  if (!sessionToken) return { ok: false, error: MSG_REACTIVATE, noSession: true };

  const result = await createShare({
    sessionToken,
    gameId: game.id,
    tableId: game.tableId,
    players: game.players.map((p) => ({ id: p.id, name: p.name })),
    rulesetId: game.rulesetId,
    settings: game.settings,
    branding: readBranding(),
    gameStartedAt: game.startedAt,
    // Guests must not be offered the round form until the seating is set.
    seatingPending: game.seatingPending === true,
    // A game already several rounds in when "Share This Game" is first
    // tapped shouldn't hand the guest a mirror that starts at zero rounds.
    totals: currentTotals(game),
    roundCount: game.rounds.length,
    roundHistory: game.rounds,
  });
  if (!result.ok) return { ok: false, error: result.error };
  saveStoredShare(result.data);
  return { ok: true, shareToken: result.data.shareToken };
}

/**
 * Main-device side of the "share this game" feature. Reuses a share already
 * created for this exact game if one is cached; otherwise mints a fresh one.
 * See `src/ui/gameShare.ts`.
 */
async function showShareGame(root: HTMLElement, game: Game, store: GameStore, error?: string): Promise<void> {
  const shared = await ensureGameShare(game);
  if (!shared.ok) {
    if (shared.noSession) return showActivation(root, store);
    showPlay(root, game, store, undefined, { kind: 'error', text: shared.error });
    return;
  }
  renderShareGame(
    root,
    buildGuestLink(shared.shareToken),
    { back: () => showPlay(root, game, store), regenerate: () => void regenerateGuestLink(root, game, store) },
    error,
  );
}

/**
 * Kills the current guest link and mints a fresh one — e.g. it went to the
 * wrong person, or a new round of guests is starting. `cached` must exist
 * here: this is only reachable by tapping a button on the screen that's
 * already showing an existing share's link.
 */
async function regenerateGuestLink(root: HTMLElement, game: Game, store: GameStore): Promise<void> {
  const cached = getStoredShare();
  if (!cached || cached.gameId !== game.id) {
    void showShareGame(root, game, store, 'This game has not been shared yet.');
    return;
  }

  const result = await regenerateShareLink(cached.ownerToken, cached.shareToken);
  if (!result.ok) {
    void showShareGame(root, game, store, result.error);
    return;
  }

  saveStoredShare({ gameId: cached.gameId, shareToken: result.data.shareToken, ownerToken: cached.ownerToken });
  void showShareGame(root, game, store);
}

/**
 * Applies whatever round proposals a guest link has queued since this was
 * last checked — the "refresh" step from the main device's side (see the
 * feature's original spec, point 7). Each submission is scored and recorded
 * exactly as if the main device had entered it directly (same `recordWin`/
 * `recordDraw` calls `play.ts` uses), using the table state (dealer,
 * prevailing wind) as it stands at the moment each one is applied — a guest
 * never knows this, so it can't be part of what they submitted.
 *
 * A submission that fails validation/scoring is left in the pending queue
 * rather than silently dropped, so the operator can see it failed and ask
 * the guest to re-record it — but is never confused with the ones that did apply.
 */
async function checkGuestSubmissions(root: HTMLElement, game: Game, store: GameStore): Promise<void> {
  const share = getStoredShare();
  if (!share || share.gameId !== game.id) {
    showPlay(root, game, store, undefined, { kind: 'error', text: 'This game has not been shared yet.' });
    return;
  }

  const pendingResult = await fetchPending(share.ownerToken);
  if (!pendingResult.ok) {
    showPlay(root, game, store, undefined, { kind: 'error', text: pendingResult.error });
    return;
  }
  noteGuestRoundsChecked();

  if (pendingResult.data.length === 0) {
    // The relay just said, exactly, that nothing is waiting: the button's count must say so too (it used to stay at its last cheap count).
    setGuestRoundsWaiting(0);
    showPlay(root, game, store, undefined, { kind: 'error', text: 'No new rounds from the shared link.' });
    return;
  }

  const appliedIds: string[] = [];
  const problems: string[] = [];
  let photosAwaited = 0;
  // Rounds the game refused (invalid hand etc.) — reported to the guest, then removed from the queue.
  const rejectedIds: string[] = [];
  // Rounds applied by an earlier check whose "tell the relay" step never got through. They are in
  // the game already: skip them here (never apply twice) and just finish telling the relay.
  const alreadyApplied = loadApplied(game.id);
  const leftover: string[] = [];

  for (const submission of pendingResult.data) {
    if (alreadyApplied.has(submission.submissionId)) {
      leftover.push(submission.submissionId);
      continue;
    }
    const table = deriveTableState(game);
    if (submission.kind === 'draw') {
      const payload = submission.payload as GuestDrawPayload;
      recordDraw(game, { dealer: game.players[table.dealerIndex]!.id, tenpai: payload.tenpai });
      appliedIds.push(submission.submissionId);
      continue;
    }

    const payload = submission.payload as GuestWinPayload;
    const winnerIndex = game.players.findIndex((p) => p.id === payload.winnerId);
    const ctx: WinContext = {
      winner: payload.winnerId,
      seatWind: seatWindOf(winnerIndex, table.dealerIndex),
      prevailingWind: table.prevailingWind,
      dealer: game.players[table.dealerIndex]!.id,
      selfDraw: payload.selfDraw,
      ...(payload.selfDraw ? {} : { discarder: payload.discarderId }),
      robbingKong: payload.robbingKong ?? false,
      kongReplacement: payload.kongReplacement ?? false,
      lastWallTile: payload.lastWallTile ?? false,
      lastDiscard: payload.lastDiscard ?? false,
      heavenlyHand: payload.heavenlyHand ?? false,
      earthlyHand: payload.earthlyHand ?? false,
      winDoubleKong: payload.winDoubleKong ?? false,
    };
    const hand: HandInput = {
      concealed: payload.concealed,
      melds: payload.melds,
      winningTile: payload.winningTile,
      flowers: payload.flowers,
      claimedSet: payload.claimedSet,
    };

    const { problems: recordProblems } = recordWin(game, hand, ctx, payload.photos);
    if (recordProblems.length > 0) {
      problems.push(recordProblems.join('; '));
      rejectedIds.push(submission.submissionId);
      continue;
    }
    if (payload.photosDeferred) {
      // The guest's phone left the photo out because its connection is slow and will send it after the
      // round. Remember which round it belongs to, so it can be attached when it arrives.
      const recorded = game.rounds[game.rounds.length - 1]!;
      addAwaitedPhoto(game.id, { submissionId: submission.submissionId, index: recorded.index, recordedAt: recorded.recordedAt });
      photosAwaited++;
    }
    // A guest's photo scan happened on their phone; the host is where the saved hand is known, so it does the comparison.
    const scan = sanitiseRecognition(payload.recognition);
    if (scan) logRecognitionResult(scan, handTiles(hand));
    appliedIds.push(submission.submissionId);
  }

  await store.save(game);
  // Written down BEFORE any network call: from here on these rounds are in the game, and a
  // dropped connection must never make the next check apply them a second time.
  markApplied(game.id, appliedIds);
  if (photosAwaited > 0) ensureAwaitedPhotoPickup(game, share.ownerToken, () => onLatePhotoAttached(game, store));

  const toConsume = [...appliedIds, ...leftover];
  let consumed = true;
  if (toConsume.length > 0) {
    const result = await consumePending(share.ownerToken, toConsume);
    consumed = result.ok;
    if (result.ok) forgetApplied(game.id, toConsume);
  }

  // Always publish, even when nothing applied — a failed submission needs
  // to reach the guest as a `lastRejection`, or that device's Refresh keeps
  // showing "submitted" for a round that was never actually recorded (see
  // relay/gameShare.ts's PublishUpdate.lastRejection). If the connection is poor this keeps
  // retrying in the background, so the guests' totals do catch up.
  const published = await publishWithRetry(
    'check',
    share.ownerToken,
    () => ({
      ...(appliedIds.length > 0
        ? {
            totals: currentTotals(game),
            roundCount: game.rounds.length,
            ...historyForGuests(game.rounds, isSlim(game.id)),
            lastRoundSummary: `${appliedIds.length} round${appliedIds.length === 1 ? '' : 's'} recorded via the shared link.`,
          }
        : {}),
      // `null` clears an earlier notice once a round has been applied cleanly (the guest redid it);
      // omitted, the notice is left as it is. (`undefined` would be dropped from the request.)
      lastRejection:
        problems.length > 0 ? { text: problems.join(' '), at: Date.now() } : appliedIds.length > 0 ? null : undefined,
    }),
    game,
  );

  // A refused round has now been reported to the guest, so it can leave the queue. Left in, it
  // would be re-tried and re-reported on every Check and keep the "waiting" count up for good.
  // Only once the notice really reached the guests: otherwise it stays, and the next Check tries again.
  let rejectedRemoved = 0;
  if (published && rejectedIds.length > 0) {
    const result = await consumePending(share.ownerToken, rejectedIds);
    if (result.ok) rejectedRemoved = rejectedIds.length;
  }

  // Still waiting: refused rounds not yet removed, and any whose "received" note did not get through.
  setGuestRoundsWaiting(
    pendingResult.data.length - appliedIds.length - (consumed ? leftover.length : 0) - rejectedRemoved,
  );

  const parts: string[] = [];
  if (appliedIds.length > 0) parts.push(`${appliedIds.length} round(s) applied from the shared link.`);
  else if (leftover.length > 0) parts.push('Those rounds were already applied earlier — nothing was counted twice.');
  if (photosAwaited > 0) {
    parts.push(
      `${photosAwaited} arrived without its photo because the guest's connection is slow — the photo will be added to the round when it arrives.`,
    );
  }
  if (problems.length > 0) {
    parts.push(
      rejectedRemoved > 0
        ? `${problems.length} could not be recorded, and the guest has been asked to redo it: ${problems.join(' ')}`
        : `${problems.length} could not be recorded and is still waiting: ${problems.join(' ')}`,
    );
  }
  if (!consumed) {
    parts.push(
      "Couldn't tell the server the rounds were received (no connection). Tap Check again once it is back — nothing will be counted twice.",
    );
  }
  if (!published) parts.push("Guests' screens will update once the connection is back.");
  const text = parts.join(' ');
  showPlay(root, game, store, undefined, {
    kind: problems.length > 0 || !consumed ? 'error' : 'success',
    text,
  });
}

/**
 * Guest side of the "share this game" feature — no activation, no local
 * `Game`/`GameStore` at all. Everything comes from the relay's public
 * mirror; every action goes back through the relay as an unvalidated
 * proposal for the main device to apply. See `src/ui/views/guestPlay.ts`.
 */
async function showGuestPlay(
  root: HTMLElement,
  shareToken: string,
  message?: { kind: 'error' | 'success'; text: string },
): Promise<void> {
  const result = await fetchShareState(shareToken);
  let state: SharedGameState;
  let offline = false;
  if (result.ok) {
    state = result.data;
    lastGuestState = { token: shareToken, state };
  } else if (result.network) {
    // Not a bad link — just no signal. Keep showing what this phone last knew rather than
    // telling the guest their link is unavailable.
    if (lastGuestState?.token === shareToken) {
      state = lastGuestState.state;
      offline = true;
    } else {
      renderNoConnection(root, () => void showGuestPlay(root, shareToken, message));
      return;
    }
  } else {
    root.innerHTML = `
      ${renderBrandHeader()}
      <div class="card">
        <h1>Link Unavailable</h1>
        <p class="muted">${esc(result.error)}</p>
      </div>
    `;
    return;
  }

  const actions = guestPlayActions(root, shareToken, state.gameId);
  renderGuestPlay(root, withHeldPhotos(state), message, actions);
  if (offline) setGuestLiveNote(root, undefined, GUEST_OFFLINE_NOTE);
  startGuestLiveUpdates(root, shareToken, state, actions);
  // A photo this phone held back from an earlier round: keep trying to send it.
  if (loadGuestPhotoJobs(state.gameId).length > 0) startGuestPhotoUpload(root, shareToken, state.gameId);
  // A round entered earlier that never got through (page reloaded, phone restarted): carry on sending it.
  resumeOutbox(root, shareToken, state.gameId);
}

const GUEST_OFFLINE_NOTE = "Can't reach the server — showing the last update. It refreshes by itself when the connection is back.";

/** The last state each guest link loaded, shown instead of an error screen when the connection drops. */
let lastGuestState: { token: string; state: SharedGameState } | undefined;

/** A plain "no signal" screen with a retry — used when nothing has loaded yet, so there is nothing else to show. */
function renderNoConnection(root: HTMLElement, retry: () => void): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>No connection</h1>
      <p class="muted">${esc(NETWORK_ERROR)}</p>
      <button type="button" id="noConnectionRetryBtn" style="width:100%;margin-top:0.5rem">Try again</button>
    </div>
  `;
  root.querySelector<HTMLButtonElement>('#noConnectionRetryBtn')!.addEventListener('click', retry);
}

/**
 * Keeps a guest's scoreboard and round list current without Refresh (see
 * `shareWatch.ts` for the cost rules). Updates happen in place, so a hand the
 * guest is entering is never wiped; only a bigger change (the game ended, the
 * host set the seating) re-renders the screen, and there is nothing half-entered
 * to lose at those points.
 */
function startGuestLiveUpdates(
  root: HTMLElement,
  shareToken: string,
  state: SharedGameState,
  actions: GuestPlayActions,
): void {
  watchSharedGame(shareToken, state, {
    isShowing: () => isGuestLiveScreen(root),
    apply: (next) => {
      lastGuestState = { token: shareToken, state: next };
      const shown = withHeldPhotos(next);
      if (updateGuestLive(root, shown)) {
        repaintHeldPhotos(root, shareToken);
        return;
      }
      renderGuestPlay(root, shown, undefined, actions);
      startGuestLiveUpdates(root, shareToken, next, actions);
    },
    linkGone: () => void showGuestPlay(root, shareToken),
    checked: (at) => setGuestLiveNote(root, at),
    problem: () => setGuestLiveNote(root, undefined, GUEST_OFFLINE_NOTE),
  });
}

function guestPlayActions(root: HTMLElement, shareToken: string, gameId: string): GuestPlayActions {
  return {
    identity: () => ({ key: shareViewerKey(shareToken), nickname: getViewer(shareViewerKey(shareToken)) }),
    submitWin: (payload, clientSubmissionId) =>
      void handleGuestSubmit(root, shareToken, gameId, 'win', payload, clientSubmissionId),
    submitDraw: (payload, clientSubmissionId) =>
      void handleGuestSubmit(root, shareToken, gameId, 'draw', payload, clientSubmissionId),
    refresh: () => void showGuestPlay(root, shareToken),
    downloadPdf: () => void handleGuestDownloadPdf(root, shareToken),
    fetchLatestState: async () => {
      const result = await fetchShareState(shareToken);
      return result.ok ? result.data : undefined;
    },
    outboxStatus: () => outboxStatusFor(gameId),
    retryOutboxNow: () => retryOutboxNow(root, shareToken, gameId),
    heldPhotos: (st) => heldPhotosStatusFor(st),
    getPhotos: () => void getPhotosNow(root, shareToken),
    ownPhotoNote: () => ownPhotoNoteFor(gameId),
  };
}

// ---------------------------------------------------------------------------
// Photos on a slow connection, guest side (see `photoDelivery.ts`): the host may hold its photos back,
// and this phone may hold its own photo back from a round it sends.
// ---------------------------------------------------------------------------

let photosBusy = false;
/** What the last "Get photos" ended with, so the card keeps saying it ("couldn't get them all — try again"). */
let photosOutcomeNote: string | undefined;
/** How this phone's own held-back photo ended (sent / could not be sent), shown once its round has gone. */
let ownPhotoOutcome: string | undefined;

/** The state as it should be shown: with any photos this phone has already fetched put back on their rounds. */
function withHeldPhotos(state: SharedGameState): SharedGameState {
  return withFetchedPhotos(state, loadedPhotosFor(state.gameId));
}

function heldPhotosStatusFor(state: SharedGameState): { text: string; canGet: boolean; busy?: boolean } | undefined {
  const total = state.photoRounds?.length ?? 0;
  if (total === 0) return undefined;
  const missing = photosStillHeld(state, loadedPhotosFor(state.gameId)).length;
  const note = missing > 0 ? photosOutcomeNote : undefined;
  const text = heldPhotosNote(state, missing, total);
  return { text: note ? `${note} ${text}` : text, canGet: missing > 0, busy: photosBusy };
}

/** Redraws the "photos held back" card from the latest state this guest link loaded. */
function repaintHeldPhotos(root: HTMLElement, shareToken: string): void {
  const current = lastGuestState;
  if (!current || current.token !== shareToken) return;
  setGuestHeldPhotos(root, heldPhotosStatusFor(current.state), () => void getPhotosNow(root, shareToken));
}

/** "Get photos": fetches what the host has sent, adds it to the round list, and says how it went. */
async function getPhotosNow(root: HTMLElement, shareToken: string): Promise<void> {
  const current = lastGuestState;
  if (!current || current.token !== shareToken || photosBusy) return;
  const gameId = current.state.gameId;
  const wanted = photosStillHeld(current.state, loadedPhotosFor(gameId));
  if (wanted.length === 0) return;

  photosBusy = true;
  photosOutcomeNote = undefined;
  repaintHeldPhotos(root, shareToken);
  const result = await getHeldPhotos(shareToken, gameId, wanted);
  photosBusy = false;

  const stillMissing = photosStillHeld(current.state, loadedPhotosFor(gameId)).length;
  if (result.expired) {
    photosOutcomeNote = 'This link has expired, so the photos can no longer be fetched.';
  } else if (result.failed) {
    photosOutcomeNote = "Couldn't get every photo — the connection is slow. Try again in a moment.";
  } else if (stillMissing > 0) {
    photosOutcomeNote = `${stillMissing} photo${stillMissing === 1 ? ' has' : 's have'} not been sent by the main device yet — try again a little later.`;
  }

  const shown = withHeldPhotos(current.state);
  if (!refreshGuestRounds(root, shown) && current.state.ended) {
    // Final Results: redraw it in place (the button to share a PDF has to be rebuilt with the photos too).
    renderGuestPlay(root, shown, undefined, guestPlayActions(root, shareToken, gameId), undefined, { fresh: false });
    return;
  }
  repaintHeldPhotos(root, shareToken);
}

function ownPhotoNoteFor(gameId: string): string | undefined {
  const waiting = loadGuestPhotoJobs(gameId).length;
  if (waiting > 0) {
    return `${waiting === 1 ? 'Your photo is' : `${waiting} of your photos are`} still waiting to be sent. ${waiting === 1 ? 'It goes' : 'They go'} automatically when the connection is better and will be added to the PDF.`;
  }
  return ownPhotoOutcome;
}

function startGuestPhotoUpload(root: HTMLElement, shareToken: string, gameId: string, immediate = false): void {
  ensureGuestPhotoUpload(
    shareToken,
    gameId,
    (outcome: GuestPhotoOutcome) => {
      ownPhotoOutcome =
        outcome === 'sent'
          ? 'Your photo was sent to the main device.'
          : outcome === 'expired'
            ? 'Your photos could not be sent because this link has expired.'
            : 'A photo could not be sent — the server would not accept it.';
      setGuestOwnPhotoNote(root, ownPhotoNoteFor(gameId));
    },
    immediate,
  );
  setGuestOwnPhotoNote(root, ownPhotoNoteFor(gameId));
}

/**
 * Takes the photo out of a round that is about to be sent (or was just sent and timed out) and queues it to be sent
 * separately, after the round. Returns the item as it will be sent now, or undefined when there was no photo to hold back.
 * Each round keeps its own place in the queue, so several poor rounds in a row lose nothing.
 */
function holdBackPhoto(item: OutboxItem): OutboxItem | undefined {
  const { payload, photos } = deferPayloadPhotos(item.payload);
  if (photos.length === 0) return undefined;
  enqueueGuestPhotoJob({ gameId: item.gameId, submissionId: item.clientSubmissionId, photos, queuedAt: Date.now() });
  const next: OutboxItem = { ...item, payload, photosHeldBack: true };
  saveOutbox(next);
  return next;
}

// ---------------------------------------------------------------------------
// A guest's round on a poor connection: saved on the phone first, then sent — and re-sent
// automatically until the relay confirms it. See `guestOutbox.ts` for why a retry can never
// record the round twice.
// ---------------------------------------------------------------------------

let outboxPoller: Poller | undefined;
let outboxSending = false;
let outboxOnlineHooked = false;

function outboxStatusFor(gameId: string): { text: string; waiting: boolean } | undefined {
  const item = loadOutbox(gameId);
  return item ? { text: describeOutbox(item, outboxSending), waiting: !outboxSending } : undefined;
}

function paintOutbox(root: HTMLElement, shareToken: string, gameId: string): void {
  setGuestOutboxBanner(root, outboxStatusFor(gameId), () => retryOutboxNow(root, shareToken, gameId));
}

function stopOutboxRetry(): void {
  outboxPoller?.stop();
  outboxPoller = undefined;
}

function startOutboxRetry(root: HTMLElement, shareToken: string, gameId: string): void {
  if (outboxPoller?.isRunning()) return;
  outboxPoller = startPoller({
    baseMs: 5_000,
    minGapMs: 3_000,
    tick: async () => ((await sendOutbox(root, shareToken, gameId)) === 'retry' ? 'error' : 'stop'),
  });
  // Coming back online is the moment to try, not the next scheduled retry.
  if (!outboxOnlineHooked) {
    outboxOnlineHooked = true;
    window.addEventListener('online', () => outboxPoller?.pokeNow());
  }
}

function retryOutboxNow(root: HTMLElement, shareToken: string, gameId: string): void {
  startOutboxRetry(root, shareToken, gameId);
  outboxPoller?.pokeNow();
}

/** After a page load: if a round was queued and never confirmed, show it and keep sending. */
function resumeOutbox(root: HTMLElement, shareToken: string, gameId: string): void {
  if (!loadOutbox(gameId)) return;
  paintOutbox(root, shareToken, gameId);
  retryOutboxNow(root, shareToken, gameId);
}

async function handleGuestSubmit(
  root: HTMLElement,
  shareToken: string,
  gameId: string,
  kind: 'win' | 'draw',
  payload: GuestWinPayload | GuestDrawPayload,
  clientSubmissionId: string,
): Promise<void> {
  // Written to this phone BEFORE anything is sent, so a dropped connection (or a closed tab)
  // can never lose the round the guest just entered.
  saveOutbox({ gameId, kind, payload, clientSubmissionId, queuedAt: Date.now(), attempts: 0 });
  // This phone submitted a round for that winner: it is (one of) the player(s) this phone plays for, for the end-of-game poster offer.
  if (kind === 'win') rememberMyPlayer(shareViewerKey(shareToken), (payload as GuestWinPayload).winnerId);
  await sendOutbox(root, shareToken, gameId);
}

/** One attempt to send the queued round. 'retry' means it did not get through but will be tried again. */
async function sendOutbox(root: HTMLElement, shareToken: string, gameId: string): Promise<'done' | 'retry'> {
  const queued = loadOutbox(gameId);
  if (!queued) return 'done';
  let item: OutboxItem = queued;

  // A send from this phone recently timed out and nothing big has got through since: do not make this round wait
  // out another timeout. Send it without its photo now; the photo joins the queue and goes when the connection allows.
  if (isConnectionSlow(gameId)) {
    const lighter = holdBackPhoto(item);
    if (lighter) item = lighter;
  }

  outboxSending = true;
  paintOutbox(root, shareToken, gameId);
  let result = await submitGuestRound(shareToken, item.kind, item.payload, item.clientSubmissionId);
  if (!result.ok && result.timedOut) {
    // The send hung — with a photo in it, it is probably too big for this connection. Send the round on its
    // own straight away (it is a few KB), and let the photo follow when the connection allows.
    markConnectionSlow(gameId);
    const lighter = holdBackPhoto(item);
    if (lighter) {
      item = lighter;
      paintOutbox(root, shareToken, gameId);
      result = await submitGuestRound(shareToken, item.kind, item.payload, item.clientSubmissionId);
    }
  }
  outboxSending = false;

  if (result.ok) {
    clearOutbox(gameId);
    // The relay now has something durably recorded under this id (freshly pending, or already
    // consumed) — see `getOrCreatePendingSubmissionId`'s doc comment for why reusing it beyond
    // this point would be wrong.
    clearPendingSubmissionId(gameId);
    // The guest has redone the round the old notice was about; stop showing it (the host clears
    // it on the relay once the new round is applied).
    if (lastGuestState?.token === shareToken) dismissRejection(gameId, lastGuestState.state.lastRejection?.at);
    stopOutboxRetry();
    const photoFollows = loadGuestPhotoJobs(gameId).length > 0;
    ownPhotoOutcome = undefined;
    await showGuestPlay(root, shareToken, {
      kind: 'success',
      text: photoFollows
        ? 'Round submitted — the main device will apply it shortly. Your photo was held back because the connection is slow; it will follow once the connection is better and be added to the PDF.'
        : 'Round submitted — the main device will apply it shortly.',
    });
    // This round has just got through, so the connection is at least working right now: send any photos that
    // were held back from earlier rounds straight away instead of waiting for the next scheduled try.
    if (photoFollows) startGuestPhotoUpload(root, shareToken, gameId, true);
    return 'done';
  }

  if (isRetryable(result)) {
    noteFailedAttempt(gameId);
    paintOutbox(root, shareToken, gameId);
    startOutboxRetry(root, shareToken, gameId);
    return 'retry';
  }

  // The relay refused it (link expired, bad request) — sending again would fail the same way.
  clearOutbox(gameId);
  stopOutboxRetry();
  await showGuestPlay(root, shareToken, { kind: 'error', text: result.error });
  return 'done';
}

/** Suggests a sensible filename when the browser's print dialog offers "Save as PDF" — mirrors `views/summary.ts`'s `printableTitle`. */
function printableGuestTitle(state: { tableId: string; lastUpdatedAt: number }): string {
  const date = new Date(state.lastUpdatedAt || Date.now()).toISOString().slice(0, 10);
  return `Mahjong ${state.tableId} — ${date}`;
}

/**
 * Producing the PDF is purely local — it has no way to notice that the main
 * device went back to the game, so this re-fetches state right before
 * generating it and only proceeds if it's still ended. If the
 * main device resumed the game, this drops back to the live recording
 * screen instead of printing a "final" result that isn't final anymore; if
 * the link has since expired (past the 1-hour post-End-Game grace period —
 * see `relay/gameShare.ts`'s `isExpired`), the state fetch itself fails and
 * that same "Link Unavailable" screen covers it.
 */
async function handleGuestDownloadPdf(root: HTMLElement, shareToken: string): Promise<void> {
  const result = await fetchShareState(shareToken);
  if (!result.ok && result.network) {
    // No signal is not an expired link; keep the results on screen and say so.
    window.alert(NETWORK_ERROR);
    return;
  }
  if (!result.ok) {
    root.innerHTML = `
      ${renderBrandHeader()}
      <div class="card">
        <h1>Link Unavailable</h1>
        <p class="muted">${esc(result.error)}</p>
      </div>
    `;
    return;
  }

  if (!result.data.ended) {
    renderGuestPlay(
      root,
      result.data,
      { kind: 'error', text: 'The main device went back to this game — it has not ended after all.' },
      guestPlayActions(root, shareToken, result.data.gameId),
    );
    return;
  }

  lastGuestState = { token: shareToken, state: result.data };
  // The host may have held its photos back (slow connection). Try to get them so the PDF has them; if some
  // still cannot be loaded, let the guest choose rather than quietly handing over a PDF without photos.
  const wanted = photosStillHeld(result.data, loadedPhotosFor(result.data.gameId));
  if (wanted.length > 0) {
    const got = await getHeldPhotos(shareToken, result.data.gameId, wanted);
    const stillMissing = photosStillHeld(result.data, loadedPhotosFor(result.data.gameId)).length;
    if (stillMissing > 0) {
      const why = got.expired
        ? 'this link has expired'
        : got.failed
          ? 'the connection is slow'
          : 'the main device has not sent them yet';
      const carryOn = window.confirm(
        `${stillMissing} photo${stillMissing === 1 ? '' : 's'} could not be loaded (${why}). Open the PDF without ${stillMissing === 1 ? 'it' : 'them'}? Cancel to try again later.`,
      );
      if (!carryOn) return;
    }
  }
  const withPhotos = withHeldPhotos(result.data);

  renderGuestPlay(root, withPhotos, undefined, guestPlayActions(root, shareToken, result.data.gameId), undefined, { fresh: false });

  // Same PDF the Share button sends (and the host's Download shows), built
  // from the state just re-fetched above and opened in the on-screen preview
  // (Save / Print / Share). The browser's print dialog is only the fallback
  // if it can't be built.
  const game = buildFakeGame(withPhotos);
  const shown = await previewFreshResultsPdf(game, summarise(game), result.data.branding ?? DEFAULT_BRANDING);
  if (shown) return;

  const previousTitle = document.title;
  document.title = printableGuestTitle(result.data);
  expandAllSettlementExplanations();
  window.print();
  document.title = previousTitle;
}

/**
 * `carryOver` is everything the form already held before tapping Scan
 * Photo — winner/discarder/self-draw, and whatever tiles were already
 * tapped in — restored on Cancel so a stray tap on "Scan Photo" can't wipe
 * out work in progress. On Confirm, only the participant selections carry
 * over; the tile-related fields come from the fresh recognition result
 * instead, since a successful scan should win over the pre-scan draft.
 */
// Photo-scan attempt limit is a cost control (each recognition call is a
// real, paid API request) — tracked per round, reset when the round in
// progress changes. Module-scoped rather than passed around: this app only
// ever has one game/table active per page load, and `showPlay` is called
// fresh for every round boundary (after a round records, after cancel,
// after confirm), so `game.rounds.length` is a reliable round identity.
let scanAttemptsRoundIndex = -1;
let scanAttemptsCount = 0;

function scanAttemptsForRound(game: Game): number {
  return scanAttemptsRoundIndex === game.rounds.length + 1 ? scanAttemptsCount : 0;
}

function recordScanAttempt(game: Game): void {
  const roundIndex = game.rounds.length + 1;
  if (scanAttemptsRoundIndex !== roundIndex) {
    scanAttemptsRoundIndex = roundIndex;
    scanAttemptsCount = 0;
  }
  scanAttemptsCount += 1;
}

async function showPhotoCapture(
  root: HTMLElement,
  game: Game,
  store: GameStore,
  resumePhoto?: string,
  carryOver?: PlayFormState,
): Promise<void> {
  const maxAttempts = await getMaxScanAttempts();
  renderPhotoCapture(
    root,
    game.tableId,
    {
      confirm: (result) => {
        // What the player had already entered is kept wherever the photo is in doubt (vision/mergeScan.ts).
        const merged = mergeScanWithEntered(
          { concealed: result.concealed, melds: result.melds, flowers: result.flowers, flaggedIndices: result.needsReviewIndices },
          carryOver && { concealed: carryOver.concealed ?? [], melds: carryOver.melds ?? [], winningTile: carryOver.winningTile, flowers: carryOver.flowers ?? [], claimedSet: carryOver.claimedSet },
        );
        showPlay(root, game, store, {
          ...carryOver, // the winner, discarder, self-draw and the ticked extras are the player's own too
          concealed: merged.concealed,
          melds: merged.melds,
          flowers: merged.flowers,
          winningTile: merged.winningTile,
          claimedSet: merged.claimedSet,
          photos: result.photos,
          needsReviewTiles: merged.stillFlagged,
          ...(merged.note ? { scanNote: merged.note } : {}),
          selfDraw: carryOver?.selfDraw,
        });
      },
      cancel: () => showPlay(root, game, store, carryOver && { ...carryOver }),
    },
    {
      maxAttempts,
      attemptsUsed: scanAttemptsForRound(game),
      onAttempt: () => recordScanAttempt(game),
    },
    resumePhoto,
  );
}

function showSummary(root: HTMLElement, game: Game, store: GameStore): void {
  resumePhotoSync(game, store);
  const summary = summarise(game);
  renderSummary(root, game, summary, {
    newGame: () => {
      localStorage.removeItem(LAST_GAME_KEY);
      showModeSelect(root, store);
    },
    resumeGame: () => {
      reopenGame(game);
      // A refused result is forgotten: ending the game again sends the new, longer game.
      if (game.leaderboard) delete game.leaderboard.rejected;
      publishGameEndedState(game, false);
      void store.save(game).then(() => showPlay(root, game, store));
    },
    viewPastGames: () => void showPastGames(root, store, () => showSummary(root, game, store)),
    saveGameFile: saveThisGame(game, store),
    guestLinkNote: guestLinkNoteFor(game),
    leaderboard: leaderboardSummaryActions(root, store, game, () => showSummary(root, game, store), true),
  });
}

/**
 * The leaderboard card on a leaderboard game's Final Results (undefined for any other game). With `auto`, an
 * owed result is sent as soon as the screen is up — this is how End Game submits — and the card updates in
 * place as it goes. `reopen` redraws this same results screen, for coming back from the board.
 */
function leaderboardSummaryActions(
  root: HTMLElement,
  store: GameStore,
  game: Game,
  reopen: () => void,
  auto: boolean,
): (LeaderboardStatusActions & { status(): SubmissionStatus }) | undefined {
  if (!game.leaderboard) return undefined;
  let sending = auto && isAwaitingSubmission(game);
  let reason: string | undefined;
  const status = (): SubmissionStatus => statusOf(game, sending, reason)!;
  const actions: LeaderboardStatusActions & { status(): SubmissionStatus } = {
    status,
    send: () => void run(),
    viewBoard: () => void showLeaderboardBoard(root, store, reopen, game.players.map((p) => p.name)),
  };
  const run = async (): Promise<void> => {
    sending = true;
    reason = undefined;
    paintLeaderboardStatus(root, game, status(), actions);
    const outcome = await sendGameResult(store, game).catch((): SubmitOutcome => ({ kind: 'retry', reason: MSG_NETWORK }));
    sending = false;
    if (outcome.kind === 'retry') reason = outcome.reason;
    if (outcome.kind === 'reactivate') reason = MSG_REACTIVATE;
    paintLeaderboardStatus(root, game, status(), actions);
  };
  // Started after the screen has been drawn (renderSummary calls status() while drawing).
  if (sending) setTimeout(() => void run(), 0);
  return actions;
}

/** The board pages (top 50, then the podium). `back` returns to wherever it was opened from; `highlight` marks these players' rows. */
async function showLeaderboardBoard(root: HTMLElement, store: GameStore, back: () => void, highlight: string[] = []): Promise<void> {
  const retry = () => void showLeaderboardBoard(root, store, back, highlight);
  renderLeaderboardMessage(root, 'Leaderboard', 'Loading the leaderboard…', undefined);
  const geo = await readLocation();
  const found = await fetchDisplayBoard(geo.ok ? geo.coords : undefined);
  if (!found.ok) {
    if (found.error === MSG_REACTIVATE) return showActivation(root, store);
    return renderLeaderboardMessage(root, 'Leaderboard', found.error, { back, retry });
  }
  if (found.access !== 'ok') {
    return renderLeaderboardMessage(root, 'Leaderboard', describeAccess(found.access), {
      back,
      ...(found.access === 'location_needed' ? { retry } : {}),
    });
  }
  if (!found.board) return renderLeaderboardMessage(root, 'No leaderboard mode right now', '', { back });
  renderWebBoard(root, { board: found.board, settings: found.display, generatedAt: Date.now() }, highlight, { back, refresh: retry });
}

/** Tells the host when the guests' links stop working (an hour after End Game), if this game was shared. */
function guestLinkNoteFor(game: Game): string | undefined {
  const share = getStoredShare();
  if (!share || share.gameId !== game.id || !game.endedAt) return undefined;
  const at = new Date(game.endedAt + GUEST_LINK_WINDOW_MS).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `Guests' links stop working at about ${at} — 1 hour after the game ended — so guests should download their PDF before then.`;
}

/**
 * Keeps a shared guest link's `ended` flag in sync with the local game —
 * this, not the guest UI alone, is what actually locks the link once the
 * main device ends the game (and un-locks it on "go back to this game"; see
 * `relay/gameShare.ts`'s `isExpired` for the 1-hour grace period after that).
 * Fire-and-forget: the local game record is always the source of truth, and
 * a relay hiccup here shouldn't block ending or resuming the game itself —
 * a guest who doesn't see the change yet will pick it up on their next
 * Refresh regardless.
 */
function publishGameEndedState(game: Game, ended: boolean): void {
  const share = getStoredShare();
  if (!share || share.gameId !== game.id) return;
  void publishWithRetry('ended', share.ownerToken, () => ({ ended, gameEndedAt: game.endedAt }));
}

/**
 * After the seating is set: the guests' copy holds the players in seating order
 * (their seat winds are derived from that order), so send the reordered list
 * along with the cleared flag. Fire-and-forget, like the other publishers.
 */
function publishSeatingToShare(game: Game): void {
  const share = getStoredShare();
  if (!share || share.gameId !== game.id) return;
  void publishState(share.ownerToken, {
    players: game.players.map((p) => ({ id: p.id, name: p.name })),
    seatingPending: game.seatingPending === true,
  });
}

/**
 * Keeps a shared guest link's mirrored totals/round count in sync whenever
 * a round is recorded or re-scored directly on this (the main) device —
 * not just when applying a guest's own submission via `checkGuestSubmissions`.
 * Without this, a guest link only ever reflected rounds that happened to
 * arrive through the guest link itself; every round the main device entered
 * normally never reached the relay's public mirror at all, so a guest's
 * Refresh silently showed stale totals. Fire-and-forget, same rationale as
 * `publishGameEndedState`.
 */
function publishGameStateToShare(game: Game): void {
  const share = getStoredShare();
  if (!share || share.gameId !== game.id) return;
  // Rebuilt from the live game on every retry, so a retry never sends a stale snapshot.
  void publishWithRetry(
    'state',
    share.ownerToken,
    () => ({
      totals: currentTotals(game),
      roundCount: game.rounds.length,
      ...historyForGuests(game.rounds, isSlim(game.id)),
    }),
    game,
  );
}

/**
 * A photo a guest sent after its round has been put on the round: keep the game, and hand the guests
 * their copy (which, once photos travel separately, uploads that round's photo).
 */
async function onLatePhotoAttached(game: Game, store: GameStore): Promise<void> {
  await store.save(game);
  publishGameStateToShare(game);
}

/** What the host is told once a slow connection made the photos travel separately (empty while they travel with the game as usual). */
function photoSyncNoteFor(game: Game): string {
  const share = getStoredShare();
  if (!share || share.gameId !== game.id || !isSlim(game.id)) return '';
  return 'Slow connection: photos are being sent to the guests separately, in the background. Guests tap Get photos to see them.';
}

/** Shows (or hides) the host's "photos are being sent separately" line on the screen that is open right now, if it has one. */
function paintPhotoSyncNote(game: Game): void {
  const el = document.getElementById('photoSyncNote');
  if (!el) return;
  const text = photoSyncNoteFor(game);
  el.textContent = text;
  el.hidden = text === '';
}

/**
 * Picks the photo work back up after a reload or when a screen opens: uploads what the guests are still owed
 * and collects photos guests promised. Does nothing for a game that isn't shared or has nothing outstanding.
 */
function resumePhotoSync(game: Game, store: GameStore): void {
  const share = getStoredShare();
  if (!share || share.gameId !== game.id) return;
  if (isSlim(game.id)) ensureRoundPhotoUploads(game, share.ownerToken);
  ensureAwaitedPhotoPickup(game, share.ownerToken, () => onLatePhotoAttached(game, store));
}

/** Per kind of update, the newest one wins: an older one still retrying gives up when a newer starts. */
const publishGenerations = new Map<string, number>();

/**
 * Sends an update to the guests' shared copy. The first attempt is awaited (so the caller can say
 * whether it worked); if the connection drops, it keeps trying in the background (3 s, 6 s, 12 s...)
 * instead of leaving the guests looking at old totals until the host happens to do something else.
 * Returns whether the first attempt got through.
 */
async function publishWithRetry(
  kind: string,
  ownerToken: string,
  build: () => PublishUpdate,
  game?: Game,
): Promise<boolean> {
  const generation = (publishGenerations.get(kind) ?? 0) + 1;
  publishGenerations.set(kind, generation);

  // A send that carries photos and TIMES OUT is too big for this connection. From then on the photos stop
  // travelling inside the game (`build` reads `isSlim` on every attempt, so the very next retry is already
  // without them) and are uploaded one round at a time instead. A plain dropped connection does not trigger
  // this: retrying the same send is right for that. See `photoDelivery.ts`.
  const afterAttempt = (update: PublishUpdate, result: Awaited<ReturnType<typeof publishState>>): void => {
    if (!game) return;
    if (!result.ok && result.timedOut && !isSlim(game.id) && (update.roundHistory ?? []).some((r) => (r.photos?.length ?? 0) > 0)) {
      setSlim(game.id);
      paintPhotoSyncNote(game);
    }
    if (isSlim(game.id)) ensureRoundPhotoUploads(game, ownerToken);
  };

  const firstUpdate = build();
  const first = await publishState(ownerToken, firstUpdate);
  afterAttempt(firstUpdate, first);
  if (first.ok) return true;
  if (!isRetryable(first)) return false;
  void retryWithBackoff(async () => {
    if (publishGenerations.get(kind) !== generation) return true; // a newer update took over
    const update = build();
    const result = await publishState(ownerToken, update);
    afterAttempt(update, result);
    return result.ok || !isRetryable(result);
  });
  return false;
}
